// API client for the Unified OS People section (ENG-2480). Talks to the
// api-getorbitx edge, which forwards to the ProspectOS PersonService. Read calls
// are GET (the edge forwards them to Connect's no-side-effect RPCs).
// Convention matches app/api/people.ts (axiosInstance + Bearer token).
import { uploadMeta } from "~/utils/files-cdn"
import { ITokenInfo } from "~/types/global"
import { axiosInstance } from "~/utils/axios"
import type {
  ICompanySuggestion,
  IEnrichResponse,
  IIcpSettings,
  IPeopleFilters,
  IPeopleListResponse,
  IPersonActivityPage,
  ICircleCapture,
  ICircleStrength,
  IMutualStatus,
  IPersonConnector,
  IPersonMutual,
  IPersonPath,
  IPersonEngagement,
  IPersonTask,
  IPersonMemberships,
  IPersonOS,
  IPersonProfile,
  IRelatedOpportunity,
  ITalkingPoint,
  IWarmConnectsResponse,
  PeopleScope,
  PeopleWarmth
} from "~/types/people-os"

const base = (t: ITokenInfo) =>
  `/tenants/${t?.tenant_id}/users/${t?.user_id}/people`
// Snoozes live under /dialer, not /people: /people/:id already owns that
// namespace, so /people/snoozes would be read as a person with that id.
const dialerBase = (t: ITokenInfo) =>
  `/tenants/${t?.tenant_id}/users/${t?.user_id}/dialer`
const auth = (t: ITokenInfo) => ({ Authorization: `Bearer ${t?.token}` })

// Edge CAPABILITY endpoints wrap bodies in createResponse -> {success, data,
// error}; unwrap so callers keep reading `res.data.<field>`. (The read
// endpoints — list/recommend/search/get/warm-connects — return raw JSON and
// must NOT be unwrapped.)
interface IEnveloped<T> {
  data: { success: boolean; data: T; error: unknown }
}
function unwrap<T>(res: unknown): { data: T } {
  return { data: (res as IEnveloped<T>).data.data }
}

// Pull the envelope's error message off an axios failure ({success:false, error}
// — a string or {message}). Callers surface this verbatim so a not-yet-deployed
// endpoint reads as the API's own 404/400 rather than a fabricated success.
export function apiErrorMessage(e: unknown, fallback: string) {
  const err = (
    e as { response?: { data?: { error?: { message?: string } | string } } }
  )?.response?.data?.error
  if (typeof err === "string" && err) return err
  if (err && typeof err === "object" && err.message) return err.message
  return fallback
}

// ProspectOS serializes proto ENUMS as their names (SOURCE_CRM, WARMTH_WARM).
// Normalize to the web's snake_case vocabulary in one place so every consumer
// (badges, filters) keeps working.
function normalizeEnums(p: IPersonOS): IPersonOS {
  const src = String(p.source || "")
    .replace(/^SOURCE_/, "")
    .toLowerCase()
  const warmth = String(p.warmth || "")
    .replace(/^WARMTH_/, "")
    .toLowerCase()
  return {
    ...p,
    source: (src === "unspecified" ? "" : src) as IPersonOS["source"],
    warmth: (warmth === "unspecified" || warmth === "no_coverage" || !warmth
      ? "none"
      : warmth) as IPersonOS["warmth"]
  }
}
function normalizeListResponse(d: IPeopleListResponse): IPeopleListResponse {
  return { ...d, people: (d.people || []).map(normalizeEnums) }
}

export interface ListPeopleArgs {
  scope: PeopleScope
  list_id?: string
  filters?: IPeopleFilters
  sort_key?: string
  sort_dir?: "asc" | "desc"
  page_size?: number
  page_token?: string
}

// GET people list for a scope (Recommended triage vs People list execution).
// promotePeople reports per-person failures here and still answers 200, so a
// caller that reads only the status code cannot tell an add that worked from
// one that wrote nothing.
export type IPromoteError = {
  person?: Partial<IPersonOS>
  reason?: string
}

export async function listPeople(t: ITokenInfo, args: ListPeopleArgs) {
  const res = (await axiosInstance({
    method: "GET",
    url: base(t),
    headers: auth(t),
    params: {
      scope: args.scope,
      list_id: args.list_id,
      sort_key: args.sort_key,
      sort_dir: args.sort_dir,
      page_size: args.page_size,
      page_token: args.page_token,
      filters: args.filters ? JSON.stringify(args.filters) : undefined
    }
  })) as { data: IPeopleListResponse }
  return { data: normalizeListResponse(res.data) }
}

// GET recommended-people pool for a set of accounts.
export async function recommendPeople(
  t: ITokenInfo,
  args: {
    account_ids: string[]
    filters?: IPeopleFilters
    page_size?: number
    page_token?: string
  }
) {
  const res = (await axiosInstance({
    method: "GET",
    url: `${base(t)}/recommend`,
    headers: auth(t),
    params: {
      account_ids: args.account_ids,
      page_size: args.page_size,
      page_token: args.page_token,
      filters: args.filters ? JSON.stringify(args.filters) : undefined
    }
  })) as { data: IPeopleListResponse }
  return { data: normalizeListResponse(res.data) }
}

// GET a single person for the Person 360 Overview.
//
// The id alone is not always resolvable: it is source-dependent (champion uuid
// for list members, m_people bigint for opportunity-rec candidates, an Apollo id
// for ICP candidates), so a Recommended row can 404/502 on lookup. Every row
// carries a linkedin_url and the backend lookup accepts one, so pass it through
// as a hint whenever the caller has it. Omitted on a direct URL visit or refresh,
// where the backend still resolves by id.
export async function getPerson(
  t: ITokenInfo,
  id: string,
  linkedinUrl?: string | null
) {
  const res = (await axiosInstance({
    method: "GET",
    url: `${base(t)}/${id}`,
    headers: auth(t),
    params: { linkedin_url: linkedinUrl || undefined }
  })) as { data: { person: IPersonOS } }
  return { data: { person: normalizeEnums(res.data.person) } }
}

// A batch-lookup person: the fields the Dialer history row actually renders
// (name, account, list). Not the full IPersonOS shape returned by getPerson —
// batchGetPeople is Node-direct (not forwarded to ProspectOS) and deliberately
// skips the expensive parts of person assembly (warmth, related opportunities)
// that a history row never shows.
export interface IPersonOSBatchEntry {
  id: string
  name: string
  title: string
  account_id: string
  account_name: string
  linkedin_url: string
  list: { id: string; name: string } | null
  // "none" for a numeric people_id row (m_people has no warmth score — only
  // champions/users_champions do) as well as a genuinely uncovered champion.
  warmth: PeopleWarmth
}

// POST people/batch { champion_ids?, people_ids? } — bulk display-data lookup
// for a page of ids at once (the Dialer history modal's rows) instead of one
// getPerson call per row. Split ids by shape (champion uuid vs numeric
// people_id) the same way the backend's personKey helpers do — the caller
// (buildBatchIds below) does this so callers of THIS function never need to
// know the distinction.
export async function getPeopleBatch(
  t: ITokenInfo,
  args: {
    championIds?: string[]
    peopleIds?: string[]
    // The list the caller is currently filtered on, if any — the List
    // column shows this membership instead of "most recently added" when
    // the champion belongs to more than one list.
    preferredListId?: string
  }
) {
  const res = await axiosInstance({
    method: "POST",
    url: `${base(t)}/batch`,
    headers: auth(t),
    data: {
      champion_ids: args.championIds || [],
      people_ids: args.peopleIds || [],
      preferred_list_id: args.preferredListId || undefined
    }
  })
  return unwrap<{ people: Record<string, IPersonOSBatchEntry> }>(res)
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const NUMERIC_ID_PATTERN = /^\d+$/

// Splits a list of raw ids (dialer_calls.person_id — champion uuid OR numeric
// people_id, same ambiguity personKeyFromRow resolves server-side) into the
// two buckets getPeopleBatch's request body wants.
//
// Global People Search (ProspectOS SearchPeople) can return a THIRD id space
// this function was never built for — Apollo ids (24-char hex, e.g.
// "5570561573696402d4c90400"), which fail the UUID check and used to fall
// straight into peopleIds unfiltered. dialer-service's people_id column is a
// bigint, so passing one of those into GET /dialer/calls's person_ids param
// threw "invalid input syntax for type bigint" — a 500 on every search whose
// results included even one non-champion, non-numeric id. Anything that is
// neither a real uuid nor a plain numeric string is dropped rather than
// guessed into a bucket it doesn't belong in.
export function buildBatchIds(ids: Array<string | null | undefined>) {
  const championIds: string[] = []
  const peopleIds: string[] = []
  for (const id of ids) {
    if (!id) continue
    if (UUID_PATTERN.test(id)) championIds.push(id)
    else if (NUMERIC_ID_PATTERN.test(id)) peopleIds.push(id)
  }
  return { championIds, peopleIds }
}

// GET manual People Search over the global graph (Search source).
export async function searchPeople(
  t: ITokenInfo,
  args: {
    query: string
    filters?: IPeopleFilters
    page_size?: number
    page_token?: string
  }
) {
  const res = (await axiosInstance({
    method: "GET",
    url: `${base(t)}/search`,
    headers: auth(t),
    params: {
      query: args.query,
      page_size: args.page_size,
      page_token: args.page_token,
      filters: args.filters ? JSON.stringify(args.filters) : undefined
    }
  })) as { data: IPeopleListResponse }
  return { data: normalizeListResponse(res.data) }
}

// GET company typeahead for the People Search Company field. Apollo's global
// company index, the same source the Pre-Meeting Brief account lookup uses, so
// a user can pick a company they do not own (ENG-2698).
export async function searchCompanies(t: ITokenInfo, query: string) {
  const res = (await axiosInstance({
    method: "GET",
    url: `${base(t)}/company-search`,
    headers: auth(t),
    params: { query, limit: 10 }
  })) as { data: { data: { companies: ICompanySuggestion[] } } }
  return { data: res.data.data }
}

// GET warm-connects (Circles) for a set of target LinkedIn URLs.
export async function getWarmConnects(
  t: ITokenInfo,
  targetLinkedinUrls: string[]
) {
  return (await axiosInstance({
    method: "GET",
    url: `${base(t)}/warm-connects`,
    headers: auth(t),
    params: { target_linkedin_urls: targetLinkedinUrls }
  })) as { data: IWarmConnectsResponse }
}

// GET the user's ICP settings (Node edge, capability — not via ProspectOS).
export async function getIcpSettings(t: ITokenInfo) {
  return unwrap<{ settings: IIcpSettings | null }>(
    await axiosInstance({
      method: "GET",
      url: `${base(t)}/icp-settings`,
      headers: auth(t)
    })
  )
}

// PUT (upsert) the user's ICP settings.
export async function updateIcpSettings(t: ITokenInfo, settings: IIcpSettings) {
  return unwrap<{ settings: IIcpSettings }>(
    await axiosInstance({
      method: "PUT",
      url: `${base(t)}/icp-settings`,
      headers: auth(t),
      data: { settings }
    })
  )
}

// ── Promotion + Lists (FR-4 / ENG-2693) ──────────────────────────────────────

export interface IPeopleList {
  id: number
  name: string
  color: string | null
  member_count: number
  created_at: string
  // Who created the list, from os_tags.user_id joined to users, falling back to
  // the owner's email when the user row carries no name. Null on a
  // tenant-shared list, which has no owner row rather than an unknown one.
  owner?: string | null
  // Members holding a phone number. Counts a non-empty phone rather than
  // is_phone_valid, which is false on every champion row today — keying on it
  // would report "0 of N" for every list. Phone rather than email because this
  // feeds the dialer, and a member with no number cannot be called.
  enriched_count?: number
  // How the list was filled, normalised by the edge to a distinct set of
  // "upload" (CSV) / "crm" / "app" (added one at a time). Empty for a list with
  // no members, and empty on the pre-V100 legacy read path, which has no
  // membership rows to report on. See LIST_SOURCE_CONFIG for the labels.
  member_sources?: string[]
  // CRM-import state, present only on lists brought across from a connected CRM.
  // `fetching`/`syncing` are in-progress; `partial` and `synced` are BOTH
  // terminal — `partial` means every member was fetched but some CRM contacts
  // have not been resolved to a person yet, which is a normal resting state, not
  // an error. Anything polling must stop on `partial` or it polls forever.
  crm_sync_status?: ICrmSyncStatus
  crm_member_total?: number
  crm_member_fetched?: number
  crm_member_linked?: number
  crm_pending_count?: number
  crm_truncated?: boolean
  crm_last_synced_at?: string | null
  crm_last_error?: string | null
}

export type ICrmSyncStatus =
  | "fetching"
  | "syncing"
  | "partial"
  | "synced"
  | "failed"

// Terminal states — see the note on crm_sync_status. Exported so the polling
// predicate and the row renderer cannot drift apart.
export const CRM_SYNC_TERMINAL: ICrmSyncStatus[] = [
  "partial",
  "synced",
  "failed"
]

// One list in the connected CRM, as offered by the import picker.
export interface ICrmList {
  crm_list_id: string
  name: string
  // DYNAMIC lists are recomputed by the CRM continuously, so an imported copy
  // drifts; STATIC/MANUAL ones do not.
  processing_type: string
  object_type_id: string
  // null when the provider did not report a size — NOT the same as an empty
  // list, and must not be rendered as 0.
  size: number | null
  imported: boolean
  list_id: number | null
  // The crm_list_map row's own id — distinct from list_id (the os_tags row).
  // This, not list_id, is what refetchCrmList takes.
  crm_list_map_id: number | null
  sync_status: ICrmSyncStatus | null
  last_synced_at: string | null
  created_at?: string
  updated_at?: string
}

// GET the connected CRM's lists for the import picker.
//
// Two modes, chosen server-side by whether `q` is present:
//   - `q` set: name search, queried LIVE against the CRM and paged by
//     `offset` (mode "search").
//   - `q` absent: the cached, newest-first set, keyset-paged by
//     `cursor_ts`/`cursor_id` (mode "cached").
// Both modes take `limit`. Follows the same args-object + `params` convention
// as searchPeople above.
export async function getCrmLists(
  t: ITokenInfo,
  args?: {
    q?: string
    cursor_ts?: string
    cursor_id?: string
    offset?: number
    limit?: number
  }
) {
  return unwrap<{
    lists: ICrmList[]
    total: number
    portal_id: string
    has_more: boolean
    mode: "search" | "cached"
    next_offset?: number
    next_cursor_ts?: string
    next_cursor_id?: string
  }>(
    await axiosInstance({
      method: "GET",
      url: `${base(t)}/crm-lists`,
      headers: auth(t),
      params: {
        q: args?.q || undefined,
        cursor_ts: args?.cursor_ts || undefined,
        cursor_id: args?.cursor_id || undefined,
        offset: args?.offset,
        limit: args?.limit
      }
    })
  )
}

export interface ICrmListImportResult {
  crm_list_id: string
  list_id: number
  name: string
  member_total: number
  truncated: boolean
  sync_status: ICrmSyncStatus
}

// POST import the selected CRM lists. Returns as soon as the lists exist —
// members arrive in the background, so the caller should expect member_count 0
// and a non-terminal crm_sync_status immediately afterwards.
export async function importCrmLists(t: ITokenInfo, crmListIds: string[]) {
  return unwrap<{
    imported: ICrmListImportResult[]
    errors: { crm_list_id: string; error: string }[]
  }>(
    await axiosInstance({
      method: "POST",
      url: `${base(t)}/crm-lists/import`,
      headers: auth(t),
      data: { crm_list_ids: crmListIds }
    })
  )
}

// POST re-snapshot an already-imported list. Additive: a person removed in the
// CRM is not removed here.
export async function refetchCrmList(t: ITokenInfo, id: number) {
  return unwrap<{ id: number; list_id: number; member_total: number }>(
    await axiosInstance({
      method: "POST",
      url: `${base(t)}/crm-lists/${id}/refetch`,
      headers: auth(t)
    })
  )
}

// POST promote people into the user's People list (Recommended "Add to list",
// search results, org chart). Idempotent server-side.
export async function addPeople(t: ITokenInfo, people: Partial<IPersonOS>[]) {
  return unwrap<{
    added: number
    champion_ids: string[]
    errors?: IPromoteError[]
  }>(
    await axiosInstance({
      method: "POST",
      url: `${base(t)}/add`,
      headers: auth(t),
      data: { people }
    })
  )
}

// GET the user's lists (os_tags PEOPLE_LIST) with member counts.
export async function getLists(t: ITokenInfo) {
  return unwrap<{ lists: IPeopleList[] }>(
    await axiosInstance({
      method: "GET",
      url: `${base(t)}/lists`,
      headers: auth(t)
    })
  )
}

// POST create a list, optionally with initial members (create-from-selection) or
// with every person from a completed CSV import (`upload_id`, the post-import
// list prompt). The people/champion_ids paths are unchanged.
export async function createList(
  t: ITokenInfo,
  args: {
    name: string
    people?: Partial<IPersonOS>[]
    champion_ids?: string[]
    people_ids?: string[]
    upload_id?: string
  }
) {
  return unwrap<{
    list: { id: number; name: string }
    added: number
    errors?: IPromoteError[]
  }>(
    await axiosInstance({
      method: "POST",
      url: `${base(t)}/lists`,
      headers: auth(t),
      data: args
    })
  )
}

// POST add members to an existing list.
export async function addToList(
  t: ITokenInfo,
  listId: number,
  args: {
    people?: Partial<IPersonOS>[]
    champion_ids?: string[]
    people_ids?: string[]
  }
) {
  return unwrap<{ added: number; errors?: IPromoteError[] }>(
    await axiosInstance({
      method: "POST",
      url: `${base(t)}/lists/${listId}/people`,
      headers: auth(t),
      data: args
    })
  )
}

// POST file every person from a completed CSV import into an existing list. The
// import's workflow_id doubles as the upload_id (it IS the champions upload
// workflow row the batch loop writes against).
export async function addUploadToList(
  t: ITokenInfo,
  listId: number,
  uploadId: string
) {
  return unwrap<{ added: number }>(
    await axiosInstance({
      method: "POST",
      url: `${base(t)}/lists/${listId}/add-upload`,
      headers: auth(t),
      data: { upload_id: uploadId }
    })
  )
}

// DELETE soft-remove members from a list. The rows keep their history; a later
// add of the same person revives the membership.
export async function removeFromList(
  t: ITokenInfo,
  listId: number,
  args: { champion_ids: string[] }
) {
  return unwrap<{ removed: number }>(
    await axiosInstance({
      method: "DELETE",
      url: `${base(t)}/lists/${listId}/people`,
      headers: auth(t),
      data: args
    })
  )
}

// POST move members off `listId` onto another list. Exactly one of
// `to_list_id` (existing list) or `name` (create, or reuse the list of that
// name) picks the target; the add and the remove run in one transaction.
export async function moveToList(
  t: ITokenInfo,
  listId: number,
  args: { champion_ids: string[]; to_list_id?: number; name?: string }
) {
  return unwrap<{
    list: { id: number; name: string }
    added: number
    removed: number
  }>(
    await axiosInstance({
      method: "POST",
      url: `${base(t)}/lists/${listId}/move`,
      headers: auth(t),
      data: args
    })
  )
}

// DELETE soft-delete a list: it disappears from the index, its memberships are
// soft-deleted and its CRM import record is removed. The people stay in the
// user's People list.
export async function deleteList(t: ITokenInfo, listId: number) {
  return unwrap<{ deleted: boolean; removed_members: number }>(
    await axiosInstance({
      method: "DELETE",
      url: `${base(t)}/lists/${listId}`,
      headers: auth(t)
    })
  )
}

// ── Capabilities (enrich / generate / brief / tags) ──────────────────────────

// POST bulk enrich (email/phone/both) — quota-gated server-side. Rows come back
// keyed on person_id (see IEnrichResponse), with an `unenriched` array carrying
// the per-person reason nothing was found.
export async function enrichPeople(
  t: ITokenInfo,
  args: { person_ids: string[]; mode: "email" | "phone" | "both" }
) {
  return unwrap<IEnrichResponse>(
    await axiosInstance({
      method: "POST",
      url: `${base(t)}/enrich`,
      headers: auth(t),
      data: args
    })
  )
}

// Message generation and the pre-meeting brief deliberately have NO people-os
// endpoints here: both reuse the platform flows directly —
//   • messages: accounts-store emailTemplateModal -> containers/account/
//     email-template.tsx (persona/custom polling + msg-template/stream SSE).
//   • brief: containers/person-brief/person-brief-drawer.tsx
//     (persona/custom + pre_meeting/generate_person_brief_bulk).

// GET the user's tags.
export async function getPeopleTags(t: ITokenInfo) {
  return unwrap<{ tags: { id: number; name: string; color: string | null }[] }>(
    await axiosInstance({
      method: "GET",
      url: `${base(t)}/tags`,
      headers: auth(t)
    })
  )
}

// POST create a tag.
export async function createPeopleTag(t: ITokenInfo, name: string) {
  return unwrap<{ tag: { id: number; name: string } }>(
    await axiosInstance({
      method: "POST",
      url: `${base(t)}/tags`,
      headers: auth(t),
      data: { name }
    })
  )
}

// ── Person 360 Overview (round 3) ────────────────────────────────────────────

// GET the Rapid-enriched profile (about / experience / skills / education) for
// the Person 360 Experience/About card. LIVE call each open — no caching.
export async function getPersonProfile(t: ITokenInfo, personId: string) {
  return unwrap<IPersonProfile>(
    await axiosInstance({
      method: "GET",
      url: `${base(t)}/${personId}/profile`,
      headers: auth(t)
    })
  )
}

// POST account-contextual talking points for the Person 360 Overview. Falls
// back to local deterministic points client-side when points come back empty.
export async function getTalkingPoints(
  t: ITokenInfo,
  personId: string,
  accountId: string
) {
  return unwrap<{ points: ITalkingPoint[] }>(
    await axiosInstance({
      method: "POST",
      url: `${base(t)}/${personId}/talking-points`,
      headers: auth(t),
      data: { account_id: accountId }
    })
  )
}

// GET the Related opportunities card. Split off the person read deliberately:
// the LLM relevance match behind it runs 29-33s, and while it lived inside
// GetPerson it blew the 20s budget in the backend's ProspectOS client, so
// Person 360 rendered NO person at all rather than one card late. Slow by
// design — the card carries its own loading state and no timeout is set here.
//
// Same linkedin_url hint as getPerson: the id alone is not always resolvable.
export async function getPersonOpportunities(
  t: ITokenInfo,
  personId: string,
  linkedinUrl?: string | null
) {
  return unwrap<{ opportunities: IRelatedOpportunity[] }>(
    await axiosInstance({
      method: "GET",
      url: `${base(t)}/${personId}/opportunities`,
      headers: auth(t),
      params: { linkedin_url: linkedinUrl || undefined }
    })
  )
}

export async function getPersonTasks(
  t: ITokenInfo,
  personId: string,
  linkedinUrl?: string | null
) {
  return unwrap<{ tasks: IPersonTask[] }>(
    await axiosInstance({
      method: "GET",
      url: `${base(t)}/${personId}/tasks`,
      headers: auth(t),
      params: { linkedin_url: linkedinUrl || undefined }
    })
  )
}

export async function getPersonEngagement(
  t: ITokenInfo,
  personId: string,
  linkedinUrl?: string | null
) {
  return unwrap<IPersonEngagement>(
    await axiosInstance({
      method: "GET",
      url: `${base(t)}/${personId}/engagement`,
      headers: auth(t),
      params: { linkedin_url: linkedinUrl || undefined }
    })
  )
}

export async function getPersonActivity(
  t: ITokenInfo,
  personId: string,
  linkedinUrl?: string | null,
  page?: { before?: string | null; limit?: number }
) {
  return unwrap<IPersonActivityPage>(
    await axiosInstance({
      method: "GET",
      url: `${base(t)}/${personId}/activity`,
      headers: auth(t),
      params: {
        linkedin_url: linkedinUrl || undefined,
        before: page?.before || undefined,
        limit: page?.limit || undefined
      }
    })
  )
}

// Fetches the people the viewer and this person both know, and returns the
// stored set. POST because it spends a LinkedIn request and writes rows.
//
// The cookie map comes from the browser extension on each call - LinkedIn
// sessions are never stored - which is also why this cannot run as a
// background sweep.
export async function fetchPersonMutuals(
  t: ITokenInfo,
  personId: string,
  cookiesMap: Record<string, string>,
  linkedinUrl?: string | null
) {
  return unwrap<{
    fetched: number
    total_reported: number
    mutuals: IPersonMutual[]
  }>(
    await axiosInstance({
      method: "POST",
      url: `${base(t)}/${personId}/circle/mutuals`,
      // Was missing, so every click returned "authorization header is not
      // present" - the only call in this file that did not send it.
      headers: auth(t),
      params: linkedinUrl ? { linkedin_url: linkedinUrl } : undefined,
      data: { cookiesMap }
    })
  )
}

// People this rep has snoozed. Champion ids only - the caller asks one
// question per row ("is this person snoozed") and ids answer it.
export type IDialerSnooze = {
  champion_id: string
  /** Last day the snooze holds, inclusive. null = no expiry. */
  snoozed_until: string | null
}

export async function getDialerSnoozes(t: ITokenInfo) {
  return unwrap<{ champion_ids: string[]; snoozes?: IDialerSnooze[] }>(
    await axiosInstance({
      // Was missing, like fetchPersonMutuals. Every read was rejected by the
      // API's authenticate middleware before reaching a controller, so no
      // snooze ever loaded.
      headers: auth(t),
      method: "GET",
      url: `${dialerBase(t)}/snoozes`
    })
  )
}

// snoozedUntil is a YYYY-MM-DD day, inclusive. Omitted means no expiry, which
// is what every snooze meant before this shipped.
export async function setDialerSnooze(
  t: ITokenInfo,
  championId: string,
  snoozed: boolean,
  reason?: string,
  snoozedUntil?: string | null
) {
  return unwrap<{ snoozed: boolean; snoozed_until?: string | null }>(
    await axiosInstance({
      headers: auth(t),
      method: snoozed ? "PUT" : "DELETE",
      url: `${dialerBase(t)}/snoozes/${championId}`,
      ...(snoozed
        ? {
            data: {
              ...(reason ? { reason } : {}),
              snoozed_until: snoozedUntil ?? null
            }
          }
        : {})
    })
  )
}

export async function getPersonCircle(
  t: ITokenInfo,
  personId: string,
  linkedinUrl?: string | null
) {
  return unwrap<{
    connectors: IPersonConnector[]
    connector_total?: number
    mutuals?: IPersonMutual[]
    mutual_total?: number
    mutual_status?: IMutualStatus | null
    best_paths?: IPersonPath[]
    strength?: ICircleStrength
    capture?: ICircleCapture
  }>(
    await axiosInstance({
      method: "GET",
      url: `${base(t)}/${personId}/circle`,
      headers: auth(t),
      params: { linkedin_url: linkedinUrl || undefined }
    })
  )
}

export async function captureLinkedinConnections(
  t: ITokenInfo,
  cookiesMap: Record<string, string>,
  connectLinkedinUrl?: string
) {
  return unwrap<Record<string, never>>(
    await axiosInstance({
      method: "POST",
      url: `/tenants/${t?.tenant_id}/users/${t?.user_id}/capture/circles/connect-details`,
      headers: auth(t),
      data: {
        cookiesMap,
        connectDetails: {
          type: "PLATFORM_USER",
          connect_linkedin_url: connectLinkedinUrl || ""
        }
      }
    })
  )
}

// ── Circles V2: import the network through a hosted provider ────────────────
//
// Same call shape as captureLinkedinConnections above, and the same cookiesMap
// from the same extension. The rep cannot tell the two apart; only what the
// backend does with the cookie differs.

export interface ICirclesProviderConnect {
  connected: boolean
  import_started: boolean
  status: string
  // False when the provider could not resolve the rep's own profile, which
  // limits warm paths to direct connections.
  connector_url_resolved?: boolean
  // Set when an import was refused because one ran recently.
  next_sync_allowed_at?: string | null
}

export interface ICirclesProviderStatus {
  // NOT_LINKED | LINK_PENDING | CONNECTED | SYNCING | SYNCED | LINK_FAILED
  status: string
  // The V1 state, and what the loader actually waits on: the import is only
  // done once the parse step has resolved accounts and moved the user to
  // CONNECTION_FETCH_COMPLETED.
  connection_fetch_state: string | null
  connector_linkedin_url: string | null
  last_synced_at: string | null
  last_sync_connection_count: number | null
  next_sync_allowed_at: string | null
}

export async function connectCirclesProvider(
  t: ITokenInfo,
  cookiesMap: Record<string, string>
) {
  return unwrap<ICirclesProviderConnect>(
    await axiosInstance({
      method: "POST",
      url: `/tenants/${t?.tenant_id}/users/${t?.user_id}/circles/provider/connect`,
      headers: auth(t),
      data: { cookiesMap }
    })
  )
}

export interface ICirclesIngestResult {
  inserted: number
  skipped: number
  next_start: number
}

export async function ingestCirclesConnectionsPage(
  t: ITokenInfo,
  body: {
    start: number
    connections: {
      firstName: string
      lastName: string
      headline: string
      publicIdentifier: string
    }[]
    more: boolean
    connector_linkedin_url: string
  }
) {
  return unwrap<ICirclesIngestResult>(
    await axiosInstance({
      method: "POST",
      url: `/tenants/${t?.tenant_id}/users/${t?.user_id}/circles/connections/page`,
      headers: auth(t),
      data: body
    })
  )
}

export async function getCirclesProviderStatus(t: ITokenInfo) {
  return unwrap<ICirclesProviderStatus>(
    await axiosInstance({
      method: "GET",
      url: `/tenants/${t?.tenant_id}/users/${t?.user_id}/circles/provider/status`,
      headers: auth(t)
    })
  )
}

// GET the lists this person belongs to + their tags (Lists & segments card).
export async function getPersonMemberships(t: ITokenInfo, personId: string) {
  return unwrap<IPersonMemberships>(
    await axiosInstance({
      method: "GET",
      url: `${base(t)}/${personId}/memberships`,
      headers: auth(t)
    })
  )
}

// POST add a tag to a person (inline "+ Add tag"). 400s when the person is not
// in a list yet — the caller surfaces the API's error message.
export async function addPersonTag(
  t: ITokenInfo,
  personId: string,
  name: string
) {
  return unwrap<{ tags: string[] }>(
    await axiosInstance({
      method: "POST",
      url: `${base(t)}/${personId}/tags`,
      headers: auth(t),
      data: { name }
    })
  )
}

// ── CSV import (ENG-2760, §4.9 Option A) ─────────────────────────────────────

export interface IImportStatus {
  workflow_id: string
  status: string // TO_START | IN_PROGRESS | SUCCESS | FAILED
  total_rows: number | null
  processed_rows: number | null
  error_rows: number | null
  /** Rows that matched a contact already in the workspace instead of creating
   *  one. Not errors and not skipped — reported so "Imported N" can tell new
   *  contacts apart from duplicates (ENG-3398). */
  merged_rows: number | null
}

// POST presign: get an S3 PUT url + file_key for the upload.
export async function importPresign(
  t: ITokenInfo,
  filename: string,
  meta?: { type: string; size: number }
) {
  return unwrap<{ file_key: string; upload_url: string }>(
    await axiosInstance({
      method: "POST",
      url: `${base(t)}/import/presign`,
      headers: auth(t),
      data: { filename, mime_type: meta?.type, size: meta?.size }
    })
  )
}

// PUT the file straight to S3 with the presigned url (no auth header — the
// signature is in the url; an Authorization header would break it).
export async function uploadImportFile(uploadUrl: string, file: File) {
  const resp = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": uploadMeta(file).type }
  })
  if (!resp.ok) throw new Error(`S3 upload failed: ${resp.status}`)
}

// POST start the import for an uploaded file_key. The edge hands the batch
// loop to the ProspectOS orchestrator and returns workflow_id immediately.
export async function startImport(
  t: ITokenInfo,
  args: { file_key: string; enrich?: boolean }
) {
  return unwrap<{ workflow_id: string; status: string }>(
    await axiosInstance({
      method: "POST",
      url: `${base(t)}/import`,
      headers: auth(t),
      data: args
    })
  )
}

// GET import progress (processed/total/errors) for the progress bar.
export async function getImportStatus(t: ITokenInfo, workflowId: string) {
  return unwrap<IImportStatus>(
    await axiosInstance({
      method: "GET",
      url: `${base(t)}/import/${workflowId}`,
      headers: auth(t)
    })
  )
}
